/**
 * Multi-select dropdown with search, chips, and optional async options.
 */

import * as React from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ChevronDown, X, Search, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { SelectOption } from './Select';

/** Chip color for selected values. */
type TagVariant = 'primary' | 'secondary' | 'accent';
/** Visual validation state for the multi-select trigger. */
type InputState = 'default' | 'error' | 'success' | 'warning';
/** Whether the options list opens above or below the trigger. */
type DropdownPlacement = 'top' | 'bottom';

/** Scroll distance from bottom to trigger load-more. */
const SCROLL_LOAD_THRESHOLD_PX = 40;

/** Props for {@link MultiSelect}. */
interface MultiSelectProps {
  label?: string;
  placeholder?: string;
  options: SelectOption[];
  value?: string[];
  onChange?: (values: string[]) => void;
  onBlur?: () => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  error?: string;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  maxVisibleTags?: number;
  tagVariant?: TagVariant;
  showSelectAll?: boolean;
  state?: InputState;
  placement?: DropdownPlacement;
  onSearch?: (query: string) => void;
  isSearchLoading?: boolean;
  searchPlaceholder?: string;
  minSearchLength?: number;
  inputSize?: 'sm' | 'md' | 'lg';
}

/** Props for {@link SelectedTag}. */
interface SelectedTagProps {
  option: SelectOption;
  tagVariant: TagVariant;
  compact?: boolean;
  onRemove: (value: string, e: React.MouseEvent) => void;
}

/** `aria-describedby` target for error or helper text. */
function getAriaDescribedBy(id: string, error?: string, helperText?: string): string | undefined {
  if (error) return `${id}-error`;
  if (helperText) return `${id}-helper`;
  return undefined;
}

/** Close the dropdown when a click lands outside `ref`. */
function useCloseOnOutsideClick(
  ref: React.RefObject<HTMLDivElement | null>,
  isOpen: boolean,
  onClose: () => void,
) {
  React.useEffect(() => {
    if (!isOpen) return;

    /** Close when the event target is outside the dropdown root. */
    const handleClick = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [ref, isOpen, onClose]);
}

/** Selected-value chip with a remove control. */
const SelectedTag: React.FC<SelectedTagProps> = ({ option, tagVariant, compact, onRemove }) => (
  <div
    className={cn(
      'modern-tag',
      compact && 'modern-tag-sm',
      tagVariant === 'primary' && 'modern-tag-primary',
      tagVariant === 'secondary' && 'modern-tag-secondary',
      tagVariant === 'accent' && 'modern-tag-accent',
    )}
  >
    <span className="modern-tag-text">{option.label}</span>
    <button
      type="button"
      className="modern-tag-remove-btn"
      onClick={(e) => onRemove(option.value, e)}
      aria-label={`Retirer ${option.label}`}
    >
      <X className="h-3 w-3" />
    </button>
  </div>
);

/** Props for {@link DropdownOption}. */
interface DropdownOptionProps {
  option: SelectOption;
  index: number;
  isSelected: boolean;
  onToggle: (value: string) => void;
}

/** One checkbox row in the dropdown list. */
const DropdownOption: React.FC<DropdownOptionProps> = ({ option, index, isSelected, onToggle }) => (
  <label
    className={cn(
      'modern-dropdown-item',
      'stagger-item',
      isSelected && 'modern-dropdown-item-selected',
      option.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
    )}
    style={{ '--stagger-index': index } as React.CSSProperties}
  >
    <Checkbox
      checked={isSelected}
      disabled={option.disabled}
      onCheckedChange={() => onToggle(option.value)}
      className="mr-3"
    />
    <span className="flex-1 text-left">{option.label}</span>
  </label>
);

/** Props for {@link SearchBar}. */
interface SearchBarProps {
  searchQuery: string;
  searchPlaceholder: string;
  isSearchLoading: boolean;
  onChange: (value: string) => void;
}

/** Filter field at the top of the dropdown. */
const SearchBar: React.FC<SearchBarProps> = ({
  searchQuery,
  searchPlaceholder,
  isSearchLoading,
  onChange,
}) => (
  <div className="px-3 py-2 border-b border-border">
    <div className="relative">
      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
      <input
        type="text"
        className="w-full pl-9 pr-9 py-1.5 text-sm rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-[var(--color-1-nuance-1)]/50"
        placeholder={searchPlaceholder}
        value={searchQuery}
        onChange={(e) => onChange(e.target.value)}
      />
      {isSearchLoading && (
        <span className="pointer-events-none absolute right-2.5 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center">
          <Loader2 className="h-4 w-4 animate-spin search-spinner" />
        </span>
      )}
    </div>
  </div>
);

/** Props for {@link SelectedTagsDisplay}. */
interface SelectedTagsDisplayProps {
  placeholder: string;
  visibleTags: SelectOption[];
  tagVariant: TagVariant;
  compact?: boolean;
  hiddenCount: number;
  showAllTags: boolean;
  onRemove: (value: string, e: React.MouseEvent) => void;
  onToggleShowAll: () => void;
}

/** Selected chips on the trigger, with overflow collapse. */
const SelectedTagsDisplay: React.FC<SelectedTagsDisplayProps> = ({
  placeholder,
  visibleTags,
  tagVariant,
  compact,
  hiddenCount,
  showAllTags,
  onRemove,
  onToggleShowAll,
}) => {
  if (visibleTags.length === 0) {
    return <span className="text-muted-foreground/60">{placeholder}</span>;
  }

  return (
    <>
      {visibleTags.map((option) => (
        <SelectedTag
          key={option.value}
          option={option}
          tagVariant={tagVariant}
          compact={compact}
          onRemove={onRemove}
        />
      ))}
      {hiddenCount > 0 && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggleShowAll();
          }}
          className={cn(
            'modern-tag modern-tag-secondary cursor-pointer',
            compact && 'modern-tag-sm',
          )}
        >
          {showAllTags ? 'Voir moins' : `+${hiddenCount} de plus`}
        </button>
      )}
    </>
  );
};

/** Props for {@link MultiSelectDropdown}. */
interface MultiSelectDropdownProps {
  listboxId: string;
  placement: DropdownPlacement;
  options: SelectOption[];
  value: string[];
  showSelectAll: boolean;
  onSearch?: (query: string) => void;
  searchQuery: string;
  searchPlaceholder: string;
  isSearchLoading: boolean;
  onSearchChange: (value: string) => void;
  onSelectAll: () => void;
  onToggle: (value: string) => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
}

/** Options panel: search, select-all, and checkbox rows. */
const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  listboxId,
  placement,
  options,
  value,
  showSelectAll,
  onSearch,
  searchQuery,
  searchPlaceholder,
  isSearchLoading,
  onSearchChange,
  onSelectAll,
  onToggle,
  onLoadMore,
  hasMore,
}) => {
  const allSelected = value.length === options.length;
  const optionsListRef = React.useRef<HTMLDivElement>(null);

  /** Trigger load-more when the user scrolls near the bottom of the options list. */
  const handleOptionsScroll = React.useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (!hasMore || !onLoadMore || isSearchLoading) return;
      const el = e.currentTarget;
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (distanceFromBottom < SCROLL_LOAD_THRESHOLD_PX) {
        onLoadMore();
      }
    },
    [hasMore, onLoadMore, isSearchLoading],
  );

  return (
    <div
      id={listboxId}
      className={cn(
        'modern-dropdown',
        placement === 'top' && 'bottom-full mb-2',
        placement === 'bottom' && 'top-full mt-2',
      )}
    >
      {onSearch && (
        <SearchBar
          searchQuery={searchQuery}
          searchPlaceholder={searchPlaceholder}
          isSearchLoading={isSearchLoading}
          onChange={onSearchChange}
        />
      )}
      {showSelectAll && options.length > 0 && (
        <>
          <button
            type="button"
            className="modern-dropdown-item w-full font-medium text-[var(--color-2-nuance-1)]"
            onClick={onSelectAll}
          >
            {allSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
          </button>
          <div className="h-px bg-border my-1" />
        </>
      )}
      {options.length === 0 ? (
        <div className="px-3 py-4 text-center text-muted-foreground text-sm">
          Aucune option disponible
        </div>
      ) : (
        <div
          ref={optionsListRef}
          className="max-h-[300px] overflow-y-auto"
          onScroll={handleOptionsScroll}
        >
          {options.map((option, index) => (
            <DropdownOption
              key={option.value}
              option={option}
              index={index}
              isSelected={value.includes(option.value)}
              onToggle={onToggle}
            />
          ))}
          {isSearchLoading && options.length > 0 && (
            <div className="flex items-center justify-center py-2 px-3">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/** Multi-select dropdown with search, chips, and optional async options. */
export const MultiSelect: React.FC<MultiSelectProps> = ({
  label,
  placeholder = 'Sélectionner des options',
  options,
  value = [],
  onChange,
  onBlur,
  onLoadMore,
  hasMore,
  error,
  helperText,
  required,
  disabled,
  className,
  maxVisibleTags,
  tagVariant = 'primary',
  showSelectAll = true,
  state,
  placement = 'bottom',
  onSearch,
  isSearchLoading = false,
  searchPlaceholder = 'Rechercher...',
  minSearchLength = 0,
  inputSize = 'md',
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [showAllTags, setShowAllTags] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const searchDebounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const multiselectId = `multiselect-${label?.toLowerCase().replaceAll(/\s+/g, '-')}`;
  const listboxId = `${multiselectId}-listbox`;

  const selectedOptions = options.filter((opt) => value.includes(opt.value));
  const shouldLimitTags = Boolean(maxVisibleTags) && !showAllTags;
  const visibleTags = shouldLimitTags ? selectedOptions.slice(0, maxVisibleTags) : selectedOptions;
  const hiddenCount = shouldLimitTags
    ? Math.max(0, selectedOptions.length - (maxVisibleTags ?? 0))
    : 0;
  const effectiveState = error ? 'error' : (state ?? 'default');

  /** Close the panel and fire `onBlur`. */
  const handleClose = React.useCallback(() => {
    setIsOpen(false);
    onBlur?.();
  }, [onBlur]);

  useCloseOnOutsideClick(dropdownRef, isOpen, handleClose);

  /** Add or remove one option value. */
  const handleToggle = (optionValue: string) => {
    if (disabled) return;
    const nextValue = value.includes(optionValue)
      ? value.filter((v) => v !== optionValue)
      : [...value, optionValue];
    onChange?.(nextValue);
  };

  /** Remove a chip without toggling the dropdown. */
  const handleRemove = (optionValue: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange?.(value.filter((v) => v !== optionValue));
  };

  /** Select every option, or clear all when already complete. */
  const handleSelectAll = () => {
    onChange?.(value.length === options.length ? [] : options.map((opt) => opt.value));
  };

  /** Update the query and debounce `onSearch`. */
  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    if (!onSearch) return;
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      if (query.length >= minSearchLength) onSearch(query);
    }, 300);
  };

  React.useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

  React.useEffect(() => {
    if (!isOpen) setSearchQuery('');
  }, [isOpen]);

  /** Enter/Space toggles the panel; Escape closes it. */
  const handleTriggerKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsOpen((open) => !open);
      return;
    }
    if (e.key === 'Escape') setIsOpen(false);
  };

  return (
    <div className={cn('w-full space-y-2', className)} ref={dropdownRef}>
      {label && (
        <Label className={cn('form-label', required && 'form-label-required')}>{label}</Label>
      )}

      <div className="relative">
        <div
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-haspopup="listbox"
          tabIndex={disabled ? -1 : 0}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          onKeyDown={handleTriggerKeyDown}
          id={multiselectId}
          aria-describedby={getAriaDescribedBy(multiselectId, error, helperText)}
          className={cn(
            'flex w-full items-center justify-between rounded-xl border-[1.5px] px-3',
            inputSize === 'sm' ? 'h-9 py-0 text-sm' : 'min-h-11 h-auto py-2 text-base',
            'ring-offset-background transition-all duration-200 cursor-pointer select-none',
            'focus:outline-none focus:ring-0',
            disabled && 'cursor-not-allowed opacity-50 pointer-events-none',
            'border-[color-mix(in_srgb,var(--color-1-nuance-3)_50%,transparent)]',
            'bg-[rgb(var(--card-rgb)/0.3)]',
            'hover:border-[var(--color-1-nuance-2)] hover:shadow-sm',
            isOpen && 'border-[var(--color-1-nuance-1)]',
            effectiveState === 'error' && 'modern-input-error',
            effectiveState === 'success' && 'modern-input-success',
            effectiveState === 'warning' && 'modern-input-warning',
          )}
        >
          <div
            className={cn(
              'flex flex-1 items-center gap-1.5 min-w-0',
              inputSize === 'sm' ? 'flex-nowrap overflow-hidden' : 'flex-wrap gap-2',
            )}
          >
            <SelectedTagsDisplay
              placeholder={placeholder}
              visibleTags={visibleTags}
              tagVariant={tagVariant}
              compact={inputSize === 'sm'}
              hiddenCount={hiddenCount}
              showAllTags={showAllTags}
              onRemove={handleRemove}
              onToggleShowAll={() => setShowAllTags(!showAllTags)}
            />
          </div>
          <ChevronDown
            className={cn(
              'h-5 w-5 shrink-0 transition-transform duration-200',
              'text-[var(--color-1-nuance-1)]',
              isOpen && 'rotate-180',
            )}
          />
        </div>

        {isOpen && (
          <MultiSelectDropdown
            listboxId={listboxId}
            placement={placement}
            options={options}
            value={value}
            showSelectAll={showSelectAll}
            onSearch={onSearch}
            searchQuery={searchQuery}
            searchPlaceholder={searchPlaceholder}
            isSearchLoading={isSearchLoading}
            onSearchChange={handleSearchChange}
            onSelectAll={handleSelectAll}
            onToggle={handleToggle}
            onLoadMore={onLoadMore}
            hasMore={hasMore}
          />
        )}
      </div>

      {error && (
        <p id={`${multiselectId}-error`} className="form-error-text" role="alert">
          {error}
        </p>
      )}
      {helperText && !error && (
        <p id={`${multiselectId}-helper`} className="form-helper-text">
          {helperText}
        </p>
      )}
    </div>
  );
};
